import { ClosingTasksInput } from '__generated__/globalTypes';
import { isValidInput } from 'sellDirect/helpers/closingDash/serviceProvidersPageCompleted';
import { YES } from 'sellDirect/components/closingDash/form/FormRadioInput';
import { OtherHomeInfoTaskFields } from 'sellDirect/helpers/__generated__/OtherHomeInfoTaskFields';
import { ServiceAccountsTaskFields } from 'sellDirect/helpers/__generated__/ServiceAccountsTaskFields';
import { ClosingTasks, Task } from 'sellDirect/helpers/closingTasks';
import { ServiceProvidersFormState } from 'sellDirect/components/closingDash/serviceProviders/ServiceProvidersForm';

/**
 * Checks if form answers are valid
 */
export const isOtherServicesFormValid = (formState: ServiceProvidersFormState): boolean => {
  return [
    formState?.recyclingCollection,
    formState?.trashCollection,
    formState?.mailboxNumber,
  ].every((item) => isValidInput(item));
};

/**
 * Checks if user has already submitted this form
 */
export const isOtherServicesFormComplete = (
  otherHomeInfoTask?: Task<OtherHomeInfoTaskFields> | null,
): boolean => {
  const otherHomeInfoTaskable = otherHomeInfoTask?.taskable as OtherHomeInfoTaskFields;
  return [
    otherHomeInfoTaskable?.recyclingCollection,
    otherHomeInfoTaskable?.trashCollection,
    otherHomeInfoTaskable?.mailboxNumber,
  ].every((item) => isValidInput(item));
};

/**
 * Checks if form answers are valid
 */
export const isUtilitiesFormValid = (formState: ServiceProvidersFormState): boolean => {
  const utilities = [formState?.electricity, formState?.water];
  if (formState?.hasGas === YES) {
    utilities.push(formState?.gas);
  }
  return utilities.every((item) => isValidInput(item));
};

/**
 * Checks if user has already submitted this form
 */
export const isUtilitiesFormComplete = (
  serviceAccountsTask: Task<ServiceAccountsTaskFields> | null | undefined,
): boolean => {
  if (!serviceAccountsTask) {
    return false;
  }
  const serviceAccountsTaskable = serviceAccountsTask?.taskable as ServiceAccountsTaskFields;
  const initialFormState = {
    electricity: serviceAccountsTaskable?.electricity ?? undefined,
    gas: serviceAccountsTaskable?.gas ?? undefined,
    hasGas: serviceAccountsTaskable?.gas ? YES : undefined,
    water: serviceAccountsTaskable?.water ?? undefined,
  };
  return isUtilitiesFormValid(initialFormState);
};

/**
 * check if service providers form is complete
 */
export const isServiceProvidersFormComplete = (closingTasks: ClosingTasks): boolean => {
  return (
    isUtilitiesFormComplete(closingTasks.serviceAccounts) &&
    isOtherServicesFormComplete(closingTasks.otherHomeInfo)
  );
};

/**
 * check if answers to service providers form is valid
 */
export const isServiceProvidersFormValid = (formState: ServiceProvidersFormState): boolean => {
  return isUtilitiesFormValid(formState) && isOtherServicesFormValid(formState);
};

/**
 * Translate service providers form state into closing tasks input
 */
export const serviceProvidersFormToClosingTasks = (
  formState: ServiceProvidersFormState,
): ClosingTasksInput => {
  return {
    otherHomeInfo: {
      additionalInfo: formState?.additionalInfo || ' ', // need the space to clear the previous answer
      mailboxNumber: formState?.mailboxNumber,
      recyclingCollection: formState?.recyclingCollection,
      trashCollection: formState?.trashCollection,
    },
    serviceAccounts: {
      electricity: formState?.electricity,
      gas: formState?.hasGas === YES ? formState?.gas : null,
      water: formState?.water,
    },
  };
};
